/**
 * Toolbar - Top application bar component
 */

import { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { ForgeAppBar, ForgeButton, ForgeIcon, ForgeIconButton } from '@tylertech/forge-react';
import '@tylertech/forge/app-bar';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import '@tylertech/forge/icon-button';
import { useNavigate } from '@tanstack/react-router';
import { useAppSelector, useAppDispatch } from '../../store';
import { selectUser, selectIsAuthenticated, logout } from '../../store/features/auth/authSlice';
import { toggleSidebar } from '../../store/features/ui/uiSlice';
import oktaAuth from '../../lib/oktaClient';
import { SearchBar } from './SearchBar';
import { TaskBadge } from '../tasks/TaskBadge';
import './Toolbar.css';

export interface ToolbarProps {
  slot?: string;
}

export default function Toolbar({ slot }: ToolbarProps): JSX.Element {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const searchRef = useRef<HTMLDivElement>(null);
  const logoRef = useRef<HTMLImageElement>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  const handleLogout = async (): Promise<void> => {
    dispatch(logout());
    if (import.meta.env.VITE_OKTA_DEV_MODE !== 'true') {
      // Clear storage first — signOut redirect may fail (CORS/no end_session_endpoint)
      // without reaching the internal tokenManager.clear() step
      oktaAuth.tokenManager.clear();
      try {
        // Identity Gateway has no /oauth2/v1/revoke endpoint — skip revocation.
        // postLogoutRedirectUri sends IdP back to root, not /auth/callback.
        await oktaAuth.signOut({
          revokeAccessToken: false,
          revokeRefreshToken: false,
          postLogoutRedirectUri: window.location.origin + '/',
        });
      } catch {
        // end-session redirect failed; tokens already cleared above — go to root
        // so initAuth() re-runs and redirects to login
        window.location.assign('/');
      }
    }
  };

  const handleMenuClick = (): void => {
    dispatch(toggleSidebar());
  };

  const handleProfileToggle = (): void => {
    setProfileOpen((prev) => !prev);
  };

  // React's JSX slot prop is not reliably forwarded to Lit shadow DOM slots on
  // re-renders. Setting it imperatively via the DOM API after every commit is robust.
  useLayoutEffect(() => {
    if (logoRef.current) logoRef.current.slot = 'logo';
    if (searchRef.current) searchRef.current.slot = 'center';
  });

  useEffect(() => {
    if (!profileOpen) return;
    const handleClickOutside = (e: MouseEvent): void => {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [profileOpen]);

  const navigate_ = (path: string): void => {
    setProfileOpen(false);
    void navigate({ to: path } as Parameters<typeof navigate>[0]);
  };

  return (
    <ForgeAppBar slot={slot} className="app-toolbar" titleText="EA Modeling">
      <img ref={logoRef} src="/logo.png" alt="EA Modeling" className="toolbar-logo" />

      <ForgeIconButton slot="start" onClick={handleMenuClick}>
        <button type="button" aria-label="Toggle menu">
          <ForgeIcon name="menu" />
        </button>
      </ForgeIconButton>

      <div ref={searchRef} className="search-slot-wrapper">
        <SearchBar />
      </div>

      <div slot="end" className="toolbar-actions">
        {isAuthenticated && user && <TaskBadge />}
        {isAuthenticated && user ? (
          <div className="profile-menu-container" ref={profileRef}>
            <button
              type="button"
              className="profile-button"
              onClick={handleProfileToggle}
              aria-label="Profile menu"
              aria-expanded={profileOpen}
              aria-haspopup="menu"
            >
              <ForgeIcon name="account_circle" />
              <span className="forge-typography--body2 profile-name">{user.name}</span>
              <ForgeIcon name={profileOpen ? 'arrow_drop_up' : 'arrow_drop_down'} />
            </button>

            {profileOpen && (
              <div className="profile-dropdown" role="menu">
                <div className="profile-dropdown-header">
                  <span className="forge-typography--caption">{user.email}</span>
                </div>
                <div className="profile-dropdown-divider" />
                <button
                  type="button"
                  className="profile-dropdown-item"
                  role="menuitem"
                  onClick={() => navigate_('/tasks')}
                >
                  <ForgeIcon name="checklist" />
                  <span>My Tasks</span>
                </button>
                <button
                  type="button"
                  className="profile-dropdown-item"
                  role="menuitem"
                  onClick={() => navigate_('/teams')}
                >
                  <ForgeIcon name="groups" />
                  <span>Teams</span>
                </button>
                <button
                  type="button"
                  className="profile-dropdown-item"
                  role="menuitem"
                  onClick={() => navigate_('/settings/api-keys')}
                >
                  <ForgeIcon name="vpn_key" />
                  <span>API Keys</span>
                </button>
                <button
                  type="button"
                  className="profile-dropdown-item"
                  role="menuitem"
                  onClick={() => navigate_('/settings/preferences')}
                >
                  <ForgeIcon name="settings" />
                  <span>Preferences</span>
                </button>
                <div className="profile-dropdown-divider" />
                <button
                  type="button"
                  className="profile-dropdown-item profile-dropdown-item--logout"
                  role="menuitem"
                  onClick={() => {
                    setProfileOpen(false);
                    void handleLogout();
                  }}
                >
                  <ForgeIcon name="logout" />
                  <span>Sign Out</span>
                </button>
              </div>
            )}
          </div>
        ) : (
          <ForgeButton type="outlined">
            <button type="button">Sign In</button>
          </ForgeButton>
        )}
      </div>
    </ForgeAppBar>
  );
}
